import { useDroppable } from '@dnd-kit/core';
import { Card } from '@/components/ui/card';
import { CanvasComponent, ProjectSettings } from '@/types';
import { useState } from 'react';
import { Rnd } from 'react-rnd';
import ComponentContextMenu from './ComponentContextMenu';

interface CanvasProps {
  components: CanvasComponent[];
  onSelect: (component: CanvasComponent | null) => void;
  onUpdate: (component: CanvasComponent) => void;
  onDelete: (componentId: string) => void;
  settings: ProjectSettings;
}

export default function Canvas({
  components,
  onSelect,
  onUpdate,
  onDelete,
  settings,
}: CanvasProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas',
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = (component: CanvasComponent) => {
    setSelectedId(component.id);
    onSelect(component);
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedId(null);
      onSelect(null);
    }
  };

  const handleZIndexChange = (id: string, action: 'front' | 'forward' | 'backward' | 'back') => {
    const sortedComponents = [...components].sort((a, b) => 
      (a.properties.zIndex || 0) - (b.properties.zIndex || 0)
    );
    
    const currentIndex = sortedComponents.findIndex(c => c.id === id);
    const component = sortedComponents[currentIndex];
    
    let newZIndex: number;
    
    switch (action) {
      case 'front':
        newZIndex = Math.max(...sortedComponents.map(c => c.properties.zIndex || 0)) + 1;
        break;
      case 'forward':
        if (currentIndex < sortedComponents.length - 1) {
          newZIndex = (sortedComponents[currentIndex + 1].properties.zIndex || 0) + 1;
        } else {
          newZIndex = component.properties.zIndex || 0;
        }
        break;
      case 'backward':
        if (currentIndex > 0) {
          newZIndex = (sortedComponents[currentIndex - 1].properties.zIndex || 0) - 1;
        } else {
          newZIndex = component.properties.zIndex || 0;
        }
        break;
      case 'back':
        newZIndex = Math.min(...sortedComponents.map(c => c.properties.zIndex || 0)) - 1;
        break;
    }

    onUpdate({
      ...component,
      properties: {
        ...component.properties,
        zIndex: newZIndex,
      },
    });
  };

  const handleDrop = (componentId: string, droppedOnId: string | null) => {
    const component = components.find(c => c.id === componentId);
    if (!component) return;

    if (droppedOnId) {
      const parent = components.find(c => c.id === droppedOnId);
      if (parent && parent.properties.isContainer) {
        onUpdate({
          ...component,
          properties: {
            ...component.properties,
            parentId: droppedOnId,
          },
        });
      }
    } else {
      onUpdate({
        ...component,
        properties: {
          ...component.properties,
          parentId: undefined,
        },
      });
    }
  };

  const renderComponents = (parentId?: string) => {
    return components
      .filter(component => component.properties.parentId === parentId)
      .map((component) => (
        <ComponentContextMenu
          key={component.id}
          component={component}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onZIndexChange={handleZIndexChange}
          components={components}
          settings={settings}
          onDrop={handleDrop}
        >
          <Rnd
            default={{
              x: component.position.x,
              y: component.position.y,
              width: component.size.width,
              height: component.size.height,
            }}
            position={{ x: component.position.x, y: component.position.y }}
            size={{ width: component.size.width, height: component.size.height }}
            onDragStop={(e, d) => {
              if (!component.properties.isLocked) {
                onUpdate({
                  ...component,
                  position: { x: d.x, y: d.y },
                });
              }
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
              if (!component.properties.isLocked) {
                onUpdate({
                  ...component,
                  position,
                  size: {
                    width: parseInt(ref.style.width),
                    height: parseInt(ref.style.height),
                  },
                });
              }
            }}
            className={`${selectedId === component.id ? 'ring-2 ring-primary' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(component);
            }}
            disableDragging={component.properties.isLocked}
            enableResizing={!component.properties.isLocked}
            style={{
              zIndex: component.properties.zIndex || 0,
              opacity: (component.properties.opacity ?? 100) / 100,
            }}
          >
            <div className="w-full h-full relative">
              <CanvasItem
                component={component}
                isSelected={selectedId === component.id}
              />
              {component.properties.isContainer && (
                <div 
                  className={`absolute inset-0 ${
                    component.properties.scrollable ? 'overflow-auto' : 'overflow-hidden'
                  }`}
                >
                  {renderComponents(component.id)}
                </div>
              )}
            </div>
          </Rnd>
        </ComponentContextMenu>
      ));
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 bg-secondary/10 m-4 relative overflow-auto border-4 border-primary/20 rounded-lg shadow-lg ${
        isOver ? 'ring-2 ring-primary' : ''
      }`}
      onClick={handleBackgroundClick}
    >
      {renderComponents()}
    </div>
  );
}

function CanvasItem({
  component,
  isSelected,
}: {
  component: CanvasComponent;
  isSelected: boolean;
}) {
  const styles = {
    width: '100%',
    height: '100%',
    backgroundColor: component.properties.styles?.backgroundColor,
    borderRadius: component.properties.styles?.borderRadius ? `${component.properties.styles.borderRadius}px` : undefined,
    borderWidth: component.properties.styles?.borderWidth ? `${component.properties.styles.borderWidth}px` : undefined,
    borderColor: component.properties.styles?.borderColor,
    borderStyle: component.properties.styles?.borderWidth ? 'solid' : undefined,
    padding: component.properties.styles?.padding ? `${component.properties.styles.padding}px` : undefined,
    textAlign: component.properties.styles?.textAlign as any,
    fontSize: component.properties.styles?.fontSize ? `${component.properties.styles.fontSize}px` : undefined,
    color: component.properties.styles?.fontColor,
    fontWeight: component.properties.styles?.fontStyles?.bold ? 'bold' : 'normal',
    fontStyle: component.properties.styles?.fontStyles?.italic ? 'italic' : 'normal',
    textDecoration: component.properties.styles?.fontStyles?.underline ? 'underline' : 'none',
  };

  const renderComponent = () => {
    switch (component.type) {
      case 'button':
        return (
          <button className="w-full h-full" style={styles}>
            {component.properties.text}
          </button>
        );
      case 'input':
        return (
          <input
            type="text"
            className="w-full h-full"
            style={styles}
            placeholder={component.properties.text}
          />
        );
      case 'text':
        return <p style={styles}>{component.properties.text}</p>;
      case 'image':
        return (
          <img
            src={component.properties.src || 'https://via.placeholder.com/150'}
            alt={component.properties.text}
            className="w-full h-full object-cover"
            style={styles}
          />
        );
      case 'card':
        return (
          <Card className="w-full h-full" style={styles}>
            {component.properties.text}
          </Card>
        );
      case 'list':
        return (
          <ul className="w-full h-full list-disc pl-4" style={styles}>
            <li>{component.properties.text}</li>
          </ul>
        );
      case 'panel':
        return (
          <div className="w-full h-full" style={styles}>
            {component.properties.text}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative w-full h-full">
      {renderComponent()}
    </div>
  );
}